import { FC } from 'react';
import { TransferOffer } from 'types';

import {
  Column,
  Description,
  IconStyled,
  Label,
  Left,
  LeftIcon,
  Right,
  Wrapper,
} from './TransactionListItem.style';

import { getIcon, TxnType, getNiceDate } from './types';

import Toastr from 'toastr2';

import { DEFAULT_FEE_TOKEN } from 'utils/constants';

import { useStarkNetSnap, useMultiLanguage } from 'services';
import { useCurrentAccount, useCurrentNetwork, useEstimateFee } from 'hooks';

import { Button } from 'components/ui/atom/Button';
import {
  Buttons,
  HeaderButton,
} from 'components/ui/organism/Header/Header.style';

import { AssetQuantity } from 'components/ui/molecule/AssetQuantity';

interface Props {
  offer: TransferOffer;
}

function shortenSender(sender: string, start = 10, end = 10) {
  const split = sender.split('::');
  const fingerprint = split[1] || sender;

  const prefix = fingerprint.slice(0, start);
  const suffix = fingerprint.slice(-end);
  return `${split[0]}::${prefix}...${suffix}`;
}

export const TransferOfferListItem: FC<Props> = ({ offer }) => {
  const toastr = new Toastr({
    closeDuration: 10000000,
    showDuration: 1000000000,
    positionClass: 'toast-top-center',
  });

  const { sendTransaction } = useStarkNetSnap();
  const { address: currentAddress } = useCurrentAccount();
  const currentNetwork = useCurrentNetwork();
  const chainId = currentNetwork?.chainId;
  const { feeEstimates } = useEstimateFee(DEFAULT_FEE_TOKEN);
  const { translate } = useMultiLanguage();

  const acceptOffer = () => {
    console.log('acceptOffer', offer);

    let fee = '1';

    if (feeEstimates) {
      fee = feeEstimates.fee;
    }

    sendTransaction(
      currentAddress,
      offer.accept?.command,
      offer.accept?.preparedTx,
      [],
      fee,
      chainId,
      DEFAULT_FEE_TOKEN,
    )
      .then((result) => {
        if (result) {
          toastr.success(translate('transactionSentSuccessfully'));
        } else {
          toastr.info(translate('transactionRejectedByUser'));
        }
      })
      .catch(() => {
        toastr.error(translate('errorSendingTransaction'));
      });
  };

  const rejectOffer = () => {
    console.log('rejectOffer', offer);

    let fee = '1';

    if (feeEstimates) {
      fee = feeEstimates.fee;
    }

    sendTransaction(
      currentAddress,
      offer.reject?.command,
      offer.reject?.preparedTx,
      [],
      fee,
      chainId,
      DEFAULT_FEE_TOKEN,
    )
      .then((result) => {
        if (result) {
          toastr.success(translate('transactionSentSuccessfully'));
        } else {
          toastr.info(translate('transactionRejectedByUser'));
        }
      })
      .catch(() => {
        toastr.error(translate('errorSendingTransaction'));
      });
  };

  const withdrawOffer = () => {
    console.log('withdrawOffer', offer);

    let fee = '1';

    if (feeEstimates) {
      fee = feeEstimates.fee;
    }

    sendTransaction(
      currentAddress,
      offer.withdraw?.command,
      offer.withdraw?.preparedTx,
      [],
      fee,
      chainId,
      DEFAULT_FEE_TOKEN,
    )
      .then((result) => {
        if (result) {
          toastr.success(translate('transactionSentSuccessfully'));
        } else {
          toastr.info(translate('transactionRejectedByUser'));
        }
      })
      .catch(() => {
        toastr.error(translate('errorSendingTransaction'));
      });
  };

  return (
    <Wrapper>
      <Left>
        <LeftIcon>
          <IconStyled
            transactionname={'transfer'}
            icon={getIcon(TxnType.ContractInteraction)}
          />
        </LeftIcon>
        <Column>
          <Label>
            {shortenSender(offer.interfaceViewValue.transfer.sender)}
          </Label>
          <Description>
            {getNiceDate(
              new Date(offer.interfaceViewValue.transfer.requestedAt),
              'en',
            )}
            <Buttons>
              {offer.accept && (
                <HeaderButton
                  onClick={acceptOffer}
                  backgroundTransparent
                  borderVisible
                >
                  Accept
                </HeaderButton>
              )}
              {offer.reject && (
                <Button
                  onClick={rejectOffer}
                  backgroundTransparent
                  borderVisible
                >
                  Reject
                </Button>
              )}
              {offer.withdraw && (
                <Button
                  onClick={withdrawOffer}
                  backgroundTransparent
                  borderVisible
                >
                  Withdraw
                </Button>
              )}
            </Buttons>
          </Description>
        </Column>
      </Left>
      <Right>
        <AssetQuantity
          currency={offer.interfaceViewValue.transfer.instrumentId.id}
          currencyValue={offer.interfaceViewValue.transfer.amount}
          USDValue={'0'}
        />
      </Right>
    </Wrapper>
  );
};
